import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  EmptyState,
  ErrorState,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { api, qs } from '../../lib/api';
import { money, num, plural, WORDS } from '../../lib/format';

interface HeadcountPoint {
  month: string;
  count: number;
  joined: number;
  left: number;
}

interface DeptRow {
  key: string;
  label: string;
  count: number;
  payroll: number;
}

interface TenureBucket {
  label: string;
  count: number;
}

interface FunnelStep {
  key: string;
  label: string;
  kind: string;
  count: number;
}

interface AbsenceMonth {
  month: string;
  recorded: number;
  absent: number;
  late: number;
}

interface Analytics {
  months: number;
  headcount: HeadcountPoint[];
  turnover: {
    left: number;
    average: number;
    rate: number | null;
    note: string | null;
  };
  byDepartment: DeptRow[];
  tenure: {
    medianMonths: number | null;
    buckets: TenureBucket[];
    note: string | null;
  };
  hiring: {
    openJobs: number;
    applications: number;
    hired: number;
    timeToHire: number | null;
    timeToHireNote: string | null;
    hireRate: number | null;
    hireRateNote: string | null;
    recruitingSpend: number;
    costPerHire: number | null;
    costPerHireNote: string;
    funnel: FunnelStep[];
  };
  absence: {
    recordedDays: number;
    absentDays: number;
    lateDays: number;
    rate: number | null;
    note: string | null;
    months: AbsenceMonth[];
  };
}

/*
 * صيغُ السنة والشهر محلّيةٌ هنا لا في WORDS.
 *
 * لا تُستعمل إلا في وسيط الأقدمية، فلا وجه لإضافتها إلى معجم الواجهة
 * المشترك من أجل موضعٍ واحد.
 */
const YEAR = { one: 'سنة واحدة', two: 'سنتان', few: 'سنوات', many: 'سنة' };
const MONTH = { one: 'شهر واحد', two: 'شهران', few: 'أشهر', many: 'شهرًا' };

/* حين لا يرسل الخادم سببًا، يبقى الفراغُ مُعلَّلًا لا مسكوتًا عنه. */
const NO_BASIS = 'لا يكفي المسجَّل لحساب هذا';

/**
 * الأقدميةُ بالشهور لا تُقرأ.
 *
 * «٣٨ شهرًا» رقمٌ يحتاج قسمةً في الذهن قبل أن يعني شيئًا، و«٣ سنوات
 * وشهران» يُفهم كما هو. والتقريبُ إلى أقرب شهرٍ مقصود: الوسيطُ قد يقع
 * بين شهرين، وكسرُ الشهر لا يضيف معنًى.
 */
function tenureText(totalMonths: number): string {
  const total = Math.max(0, Math.round(totalMonths));
  if (total === 0) return 'أقلّ من شهر';

  const years = Math.floor(total / 12);
  const rest = total % 12;
  const y = years > 0 ? plural(years, YEAR) : '';
  const m = rest > 0 ? plural(rest, MONTH) : '';
  if (y && m) return `${y} و${m}`;
  return y || m;
}

/** «2025-03» → «03/25» — تسميةٌ تسع تحت عمودٍ ضيّق. */
function monthTick(month: string): string {
  return `${month.slice(5)}/${month.slice(2, 4)}`;
}

/**
 * الرقمُ الغائبُ يقول سببَ غيابه.
 *
 * صفرٌ مكانَ «لم تكتمل العيّنة» كذبةٌ صريحة: من يقرأ «معدّل الدوران 0%»
 * يفهم أنّ أحدًا لم يترك العمل، والحقيقةُ أنّ الشهور المسجَّلة أقلُّ من
 * أن تُحسب. والشرطةُ «—» أقلُّ كذبًا وأقلُّ نفعًا — تقول «لا شيء» ولا
 * تقول لماذا. فيُكتب نصُّ الخادم في موضع الرقم.
 */
function Figure({
  value,
  note,
  format,
}: {
  value: number | null;
  note: string | null;
  format: (n: number) => string;
}) {
  if (value === null) {
    return <span className="tiny mute">{note ?? NO_BASIS}</span>;
  }
  return <span className="num">{format(value)}</span>;
}

/**
 * منحنى العدد عبر الشهور.
 *
 * مرسومٌ بـ SVG خامٍ لا بمكتبة رسم: لا مكتبةَ رسمٍ في المشروع، وإضافةُ
 * واحدةٍ من أجل منحنًى واحدٍ ثمنٌ في حجم الحزمة لا يُقابله عائد.
 *
 * والألوانُ متغيّراتٌ لا قيم: الشاشةُ تُقلب إلى الوضع الداكن، ولونٌ
 * مكتوبٌ صراحةً يبقى على حاله فيختفي المنحنى في خلفيّته.
 */
function HeadcountChart({ rows }: { rows: HeadcountPoint[] }) {
  const W = 760;
  const H = 262;
  const PAD = 14;
  const TOP = 20; /* أعلى ما يبلغه المنحنى */
  const BASE = 152; /* قاعُ المنحنى — الصفر */
  const ZERO = 200; /* خطُّ الحركة: الداخلُ فوقه والخارجُ تحته */
  const MOVE = 30; /* أقصى ارتفاعٍ لعمود الحركة في كلّ اتجاه */
  const LABEL = 246;

  const n = rows.length;
  const slot = (W - PAD * 2) / n;

  /*
   * الزمنُ يجري من اليمين إلى اليسار.
   *
   * الشاشةُ كلّها تُقرأ من اليمين، فلو بدأ أقدمُ شهرٍ من اليسار لقرأ
   * القارئُ المنحنى معكوسًا: يظنّ أنّ العدد ينزل وهو يصعد.
   */
  const x = (i: number) => W - PAD - slot * (i + 0.5);

  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  const y = (c: number) => BASE - (c / maxCount) * (BASE - TOP);

  const maxMove = Math.max(1, ...rows.map((r) => Math.max(r.joined, r.left)));
  /* عمودٌ لا يقلّ عن بكسلين: داخلٌ واحدٌ في شهرٍ يجب أن يُرى. */
  const moveH = (v: number) =>
    v === 0 ? 0 : Math.max(2, (v / maxMove) * MOVE);

  const points = rows.map(
    (r, i) => `${x(i).toFixed(1)},${y(r.count).toFixed(1)}`,
  );
  const line = `M ${points.join(' L ')}`;
  const area = `${line} L ${x(n - 1).toFixed(1)},${BASE} L ${x(0).toFixed(1)},${BASE} Z`;

  const barW = Math.min(14, Math.max(3, slot * 0.5));

  /* أربعٌ وعشرون تسميةً تتراكب، فتُكتب واحدةٌ كلَّ خطوة — وآخرُ شهرٍ دائمًا. */
  const step = Math.max(1, Math.ceil(n / 8));

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="عدد الموظّفين شهرًا بشهر، ومعه من دخل ومن خرج في كلّ شهر"
      /* عرضٌ كاملٌ وارتفاعٌ تابع: البطاقةُ تضيق على الشاشة الصغيرة فيضيق معها. */
      style={{
        width: '100%',
        height: 'auto',
        display: 'block',
        direction: 'ltr',
      }}
    >
      <line
        x1={PAD}
        x2={W - PAD}
        y1={TOP}
        y2={TOP}
        stroke="var(--line)"
        strokeWidth="1"
        strokeDasharray="3 5"
      />
      <text x={PAD} y={TOP - 6} fontSize="11" fill="var(--ink-3)">
        {num(maxCount)}
      </text>
      <line
        x1={PAD}
        x2={W - PAD}
        y1={BASE}
        y2={BASE}
        stroke="var(--line)"
        strokeWidth="1"
      />

      {n > 1 && <path d={area} fill="var(--brand)" fillOpacity="0.12" />}
      {n > 1 && (
        <path
          d={line}
          fill="none"
          stroke="var(--brand)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {rows.map((r, i) => (
        <circle
          key={`p-${r.month}`}
          cx={x(i)}
          cy={y(r.count)}
          r={n > 18 ? 1.8 : 2.6}
          fill="var(--brand)"
        />
      ))}

      <line
        x1={PAD}
        x2={W - PAD}
        y1={ZERO}
        y2={ZERO}
        stroke="var(--line)"
        strokeWidth="1"
      />
      {rows.map((r, i) => (
        <g key={`m-${r.month}`}>
          {r.joined > 0 && (
            <rect
              x={x(i) - barW / 2}
              y={ZERO - moveH(r.joined)}
              width={barW}
              height={moveH(r.joined)}
              rx="1"
              fill="var(--success)"
            />
          )}
          {r.left > 0 && (
            <rect
              x={x(i) - barW / 2}
              y={ZERO}
              width={barW}
              height={moveH(r.left)}
              rx="1"
              fill="var(--danger)"
            />
          )}
        </g>
      ))}

      {rows.map((r, i) =>
        (n - 1 - i) % step === 0 ? (
          <text
            key={`t-${r.month}`}
            x={x(i)}
            y={LABEL}
            fontSize="10"
            textAnchor="middle"
            fill="var(--ink-3)"
          >
            {monthTick(r.month)}
          </text>
        ) : null,
      )}

      {/*
        طبقةُ التلميح آخرَ الرسم لا أوّله.
        المستطيلُ الشفّاف يلتقط المؤشّر، ولو رُسم أوّلًا لغطّاه المنحنى.
      */}
      {rows.map((r, i) => (
        <g key={`h-${r.month}`}>
          <title>
            {`${r.month} — العدد ${num(r.count)} · دخل ${num(r.joined)} · خرج ${num(r.left)}`}
          </title>
          <rect
            x={x(i) - slot / 2}
            y={TOP - 10}
            width={slot}
            height={H - TOP}
            fill="transparent"
          />
        </g>
      ))}
    </svg>
  );
}

/** قطعةُ لونٍ في دليل الرسم. */
function Swatch({ color }: { color: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 4,
        borderRadius: 2,
        /* لا ينكمش في صفٍّ ضيّق فيصير خيطًا لا يُميَّز لونُه. */
        flexShrink: 0,
        background: color,
      }}
    />
  );
}

/**
 * تحليلاتُ الموارد البشرية.
 *
 * كلُّ رقمٍ هنا مشتقٌّ من العمل اليوميّ لا من دفترٍ منفصل: العددُ من
 * تواريخ التعيين وانتهاء الخدمة، والغيابُ من سجلّ الحضور، والتوظيفُ من
 * أختام الطلبات. وهذا هو مصدرُ قوّتها وحدِّها معًا — فما لم يُدخل في يومه
 * لا يظهر هنا، وقد بدا لمن يقرأ أنّه لم يقع.
 */
export function HrAnalytics() {
  const [months, setMonths] = useState('12');

  const query = { months };

  const stats = useQuery({
    queryKey: ['hr', 'org', 'analytics', query],
    queryFn: () => api<Analytics>(`/hr/org/analytics${qs(query)}`),
  });

  const a = stats.data;
  const hc = a?.headcount ?? [];
  const current = hc.length > 0 ? hc[hc.length - 1]!.count : 0;
  /* لا أحدَ في السجلّ أصلًا — لا فراغَ بيانات بل شركةٌ لم تُدخل موظّفيها. */
  const hasAnyone = current > 0 || (a?.byDepartment.length ?? 0) > 0;

  const maxDept = Math.max(1, ...(a?.byDepartment ?? []).map((d) => d.count));
  const maxBucket = Math.max(
    1,
    ...(a?.tenure.buckets ?? []).map((b) => b.count),
  );
  const maxFunnel = Math.max(
    1,
    ...(a?.hiring.funnel ?? []).map((f) => f.count),
  );

  return (
    <div className="page">
      <PageHead
        title="تحليلات الموارد البشرية"
        subtitle="العدد والدوران والأقدمية والتوظيف والغياب — من واقع ما أُدخل يومًا بيوم"
        actions={
          <Link className="btn" to="/hr/org/planning">
            <Icon name="gantt" size={15} />
            تخطيط القوى العاملة
          </Link>
        }
      />

      {stats.isError && (
        <ErrorState error={stats.error} onRetry={() => void stats.refetch()} />
      )}

      {/*
        الحدُّ يُكتب في الشاشة لا يُترك للحدس.

        هذه الشاشةُ ليس لها مصدرٌ خاصٌّ بها تُملأ منه: تُحسب من نفس الصفوف
        التي يكتبها الناسُ في عملهم. فمن لم يُسجَّل تعيينُه بتاريخه، ومن
        مرّت أيّامه بلا حضورٍ مُدخَل، ومن تقدّم لوظيفةٍ فلم يُختم طلبُه —
        كلُّ أولئك غائبون عن الأرقام هنا، لا لأنّهم لم يقعوا بل لأنّهم لم
        يُكتبوا. والرقمُ الناقصُ أخطرُ من الرقم الغائب، لأنّه يُقرأ واثقًا.
      */}
      <Notice title="هذه أرقامُ العمل اليوميّ لا دفترٍ منفصل" icon="info">
        لا يوجد سجلٌّ مستقلٌّ للتحليلات يُملأ على حدة. العددُ محسوبٌ من تواريخ
        التعيين وانتهاء الخدمة، والغيابُ من صفوف الحضور، والتوظيفُ من أختام
        الطلبات على مراحلها. فدقّةُ ما تقرؤه هنا هي دقّةُ الإدخال اليوميّ نفسه —
        وما لم يُدخل في يومه لن يظهر، وسيبدو وكأنّه لم يحدث. وحيث لا تكفي
        العيّنةُ للحساب يُكتب السببُ مكان الرقم، ولا يُكتب صفرٌ يُظنّ حقيقة.
      </Notice>

      <div className="toolbar">
        <select
          className="select"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
        >
          <option value="6">آخر 6 أشهر</option>
          <option value="12">آخر 12 شهرًا</option>
          <option value="24">آخر 24 شهرًا</option>
        </select>
      </div>

      {stats.isPending && <div className="skeleton" style={{ height: 200 }} />}

      {a && !hasAnyone && (
        <EmptyState
          title="لا موظّفين في السجلّ بعد"
          text="التحليلاتُ مرآةُ ما أُدخل. أضِف الموظّفين بتواريخ تعيينهم أوّلًا، ثمّ يبدأ العدُّ من تلقائه."
          action={
            <Link className="btn btn-primary" to="/hr/employees">
              الموظّفون
            </Link>
          }
        />
      )}

      {a && hasAnyone && (
        <>
          <div className="stats">
            <Stat
              label="العدد الحالي"
              value={<span className="num">{num(current)}</span>}
              note={
                hc.length > 0
                  ? `في ${monthTick(hc[hc.length - 1]!.month)}`
                  : undefined
              }
            />
            <Stat
              label="معدّل الدوران السنوي"
              value={
                <Figure
                  value={a.turnover.rate}
                  note={a.turnover.note}
                  format={(n) => `${num(n)}%`}
                />
              }
              note={
                a.turnover.rate === null
                  ? undefined
                  : `${num(a.turnover.left)} خرجوا من متوسّط ${num(a.turnover.average)}`
              }
            />
            <Stat
              label="وسيط الأقدمية"
              value={
                <Figure
                  value={a.tenure.medianMonths}
                  note={a.tenure.note}
                  format={(n) => tenureText(n)}
                />
              }
              note={
                a.tenure.medianMonths === null
                  ? undefined
                  : 'نصفُهم أقدمُ من ذلك'
              }
            />
            <Stat
              label="معدّل الغياب"
              value={
                <Figure
                  value={a.absence.rate}
                  note={a.absence.note}
                  format={(n) => `${num(n)}%`}
                />
              }
              note={
                a.absence.rate === null
                  ? undefined
                  : `${plural(a.absence.recordedDays, WORDS.day)} في السجلّ`
              }
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">العدد عبر الشهور</h2>
              <span
                className="row tiny mute"
                style={{
                  marginInlineStart: 'auto',
                  gap: 'var(--sp-3)',
                  flexWrap: 'wrap',
                }}
              >
                <span className="row" style={{ gap: 5 }}>
                  <Swatch color="var(--brand)" />
                  العدد
                </span>
                <span className="row" style={{ gap: 5 }}>
                  <Swatch color="var(--success)" />
                  دخلوا
                </span>
                <span className="row" style={{ gap: 5 }}>
                  <Swatch color="var(--danger)" />
                  خرجوا
                </span>
              </span>
            </div>
            <div className="card-body">
              {hc.length === 0 ? (
                <p className="tiny mute center">لا شهورَ في المدى المختار.</p>
              ) : (
                <>
                  <HeadcountChart rows={hc} />
                  <p className="tiny mute mt-2">
                    العددُ في آخر كلّ شهر. الأعمدةُ فوق الخطّ من دخل فيه، وتحته
                    من خرج — ومن دخل وخرج في الشهر نفسه يظهر في الاثنين ولا
                    يغيّر العدد.
                  </p>
                </>
              )}
            </div>
          </div>

          <div className="two-col">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">التوزيع على الأقسام</h2>
              </div>
              <div className="card-body">
                {a.byDepartment.length === 0 ? (
                  <p className="tiny mute center">
                    لم يُنسب أحدٌ إلى قسمٍ بعد.
                  </p>
                ) : (
                  <div className="col" style={{ gap: 'var(--sp-3)' }}>
                    {a.byDepartment.map((d) => (
                      <div key={d.key}>
                        <div
                          className="row"
                          style={{ justifyContent: 'space-between' }}
                        >
                          <span className="small strong">{d.label}</span>
                          <span className="tiny mute">
                            <span className="num">{num(d.count)}</span> ·{' '}
                            <span className="num">{money(d.payroll)}</span>
                          </span>
                        </div>
                        <div className="bar-track mt-2">
                          <span
                            className="bar-fill"
                            style={{ width: `${(d.count / maxDept) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">الأقدمية</h2>
              </div>
              <div className="card-body">
                {a.tenure.buckets.length === 0 ? (
                  <p className="tiny mute center">لا تواريخَ تعيينٍ مسجَّلة.</p>
                ) : (
                  <div className="col" style={{ gap: 'var(--sp-3)' }}>
                    {a.tenure.buckets.map((b) => (
                      <div key={b.label}>
                        <div
                          className="row"
                          style={{ justifyContent: 'space-between' }}
                        >
                          <span className="small">{b.label}</span>
                          <span className="tiny mute num">{num(b.count)}</span>
                        </div>
                        <div className="bar-track mt-2">
                          <span
                            className="bar-fill"
                            style={{ width: `${(b.count / maxBucket) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">التوظيف</h2>
            </div>
            <div className="card-body">
              <div className="stats">
                <Stat
                  label="وظائف مفتوحة"
                  value={<span className="num">{num(a.hiring.openJobs)}</span>}
                />
                <Stat
                  label="طلبات"
                  value={
                    <span className="num">{num(a.hiring.applications)}</span>
                  }
                  note={`في آخر ${plural(a.months, MONTH)}`}
                />
                <Stat
                  label="عُيّنوا"
                  value={<span className="num">{num(a.hiring.hired)}</span>}
                />
                <Stat
                  label="زمن التعيين"
                  value={
                    <Figure
                      value={a.hiring.timeToHire}
                      note={a.hiring.timeToHireNote}
                      format={(n) => plural(Math.round(n), WORDS.day)}
                    />
                  }
                  note={
                    a.hiring.timeToHire === null
                      ? undefined
                      : 'من الطلب إلى القبول'
                  }
                />
                <Stat
                  label="نسبة القبول"
                  value={
                    <Figure
                      value={a.hiring.hireRate}
                      note={a.hiring.hireRateNote}
                      format={(n) => `${num(n)}%`}
                    />
                  }
                  note={
                    a.hiring.hireRate === null
                      ? undefined
                      : 'من القرارات المحسومة'
                  }
                />
                {/*
                  كلفةُ التعيين أرضيّةٌ لا رقمٌ كامل: ما يُجمع ميزانيّاتُ
                  الحملات وحدَها. وحدُّها يُقال تحت الرقم لا في دليلٍ يُقرأ
                  مرّة — ومن يقرؤه بلا هذه الجملة يظنّ أنّه يعرف ما أنفق.
                */}
                <Stat
                  label="كلفة التعيين"
                  value={
                    <Figure
                      value={a.hiring.costPerHire}
                      note={a.hiring.costPerHireNote}
                      format={(n) => money(n)}
                    />
                  }
                  note={
                    a.hiring.costPerHire === null
                      ? undefined
                      : a.hiring.costPerHireNote
                  }
                />
              </div>

              {a.hiring.recruitingSpend > 0 && (
                <p className="tiny mute mt-3">
                  ما أُنفق في المدّة: {money(a.hiring.recruitingSpend)} من
                  ميزانيّات الحملات.
                </p>
              )}

              {a.hiring.funnel.length === 0 ? (
                <p className="tiny mute center mt-4">
                  لا طلباتِ توظيفٍ في المدى المختار.
                </p>
              ) : (
                <div className="col mt-4" style={{ gap: 'var(--sp-3)' }}>
                  {a.hiring.funnel.map((f) => (
                    <div key={f.key}>
                      <div
                        className="row"
                        style={{ justifyContent: 'space-between' }}
                      >
                        <Badge kind={f.kind}>{f.label}</Badge>
                        <span className="tiny mute num">{num(f.count)}</span>
                      </div>
                      <div className="bar-track mt-2">
                        <span
                          className="bar-fill"
                          style={{ width: `${(f.count / maxFunnel) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="card-title">الغياب</h2>
            </div>
            <div className="card-body">
              <div className="stats">
                <Stat
                  label="أيام مسجَّلة"
                  value={
                    <span className="num">{num(a.absence.recordedDays)}</span>
                  }
                  note="ما دخل سجلّ الحضور فعلًا"
                />
                <Stat
                  label="أيام غياب"
                  value={
                    <span className="num">{num(a.absence.absentDays)}</span>
                  }
                />
                <Stat
                  label="أيام تأخير"
                  value={<span className="num">{num(a.absence.lateDays)}</span>}
                />
              </div>

              {/*
                يومٌ بلا صفٍّ ليس يومَ حضور.

                الجدولُ يعرض المسجَّل وحده، وشهرٌ لم يُسجَّل فيه شيء يُقال
                فيه ذلك صراحةً — لا يُكتب فيه «0 غياب» فيُقرأ شهرًا كاملًا
                بلا تخلّف عن العمل.
              */}
              {a.absence.months.length === 0 ? (
                <p className="tiny mute center mt-4">
                  لا صفوفَ حضورٍ في المدى المختار.
                </p>
              ) : (
                <div className="table-wrap mt-4">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>الشهر</th>
                        <th>أيام مسجَّلة</th>
                        <th>غياب</th>
                        <th>تأخير</th>
                      </tr>
                    </thead>
                    <tbody>
                      {a.absence.months.map((m) => (
                        <tr key={m.month}>
                          <td className="num cell-strong">{m.month}</td>
                          <td className="num">{num(m.recorded)}</td>
                          <td className="num">
                            {m.recorded === 0 ? (
                              <span className="tiny mute">لم يُسجَّل شيء</span>
                            ) : (
                              num(m.absent)
                            )}
                          </td>
                          <td className="num cell-mute">
                            {m.recorded === 0 ? (
                              <span className="tiny mute">لم يُسجَّل شيء</span>
                            ) : (
                              num(m.late)
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
